import React, { type ReactElement } from 'react';
import memoizeOne from 'memoize-one';
import ArrowDownIcon from '@atlaskit/icon/glyph/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/glyph/arrow-up';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { IntlShape } from '@atlassian/jira-intl';
// eslint-disable-next-line jira/import/no-restricted-paths
import messages from '../../../ui/issue-table/header/header-cell/header-cell-actions/sort-button/messages';
import { SORT_ASC, SORT_DESC, SORT_NONE } from '../../constants';
import type { SortOrder } from '../../types';

type TooltipSuffixArg = { ascText?: string; descText?: string };

type SortOrderConfig = Record<
	SortOrder,
	{
		getNextState: () => SortOrder;
		getIcon: () => ReactElement;
		isIconPersistent: boolean;
		getTooltipSuffix: (arg1: TooltipSuffixArg) => string;
	}
>;

// TODO: delete this file when cleaning up jsv-one-issue-table-spreadsheet-ui_ygrnf

const getSortOrderConfigs: (intl: IntlShape) => SortOrderConfig = memoizeOne(
	({ formatMessage }) => ({
		[SORT_ASC]: {
			getNextState: () => SORT_DESC,
			getIcon: () => (
				<ArrowUpIcon label={formatMessage(messages.sortedDefaultAscendingTooltip)} size="small" />
			),
			isIconPersistent: true,
			getTooltipSuffix: ({ ascText }: TooltipSuffixArg) =>
				ascText ?? false
					? formatMessage(messages.sortedTooltip, { sortDescription: ascText })
					: formatMessage(messages.sortedDefaultAscendingTooltip),
		},
		[SORT_DESC]: {
			getNextState: () => SORT_ASC,
			getIcon: () => (
				<ArrowDownIcon
					label={formatMessage(messages.sortedDefaultDescendingTooltip)}
					size="small"
				/>
			),
			isIconPersistent: true,
			getTooltipSuffix: ({ descText }: TooltipSuffixArg) =>
				descText ?? false
					? formatMessage(messages.sortedTooltip, { sortDescription: descText })
					: formatMessage(messages.sortedDefaultDescendingTooltip),
		},
		[SORT_NONE]: {
			getNextState: () => SORT_ASC,
			getIcon: () => (
				<ArrowUpIcon
					label={formatMessage(messages.sortPromptDefaultTooltip)}
					size="small"
					primaryColor={token('color.icon.subtle', colors.N60)}
				/>
			),
			isIconPersistent: false,
			getTooltipSuffix: ({ ascText }: TooltipSuffixArg) =>
				ascText ?? false
					? formatMessage(messages.sortPromptTooltip, { sortDescription: ascText })
					: formatMessage(messages.sortPromptDefaultTooltip),
		},
	}),
);

export const getSortOrderConfig = ({
	sortOrder,
	intl,
}: {
	sortOrder: SortOrder;
	intl: IntlShape;
}) => getSortOrderConfigs(intl)[sortOrder];
